import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/theme/utils'

const uiBadgeVariants = cva(
  'inline-flex items-center whitespace-nowrap rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        accent:
          'border-primary-highlight bg-primary-accent text-primary-accent-foreground',
        success: cn(
          'relative overflow-hidden border border-[#16D1D6] bg-transparent text-foreground',
          'before:absolute before:inset-0 before:-z-[1] before:opacity-100',
          'before:bg-[radial-gradient(circle_at_top,rgba(22,209,214,0.18)_0%,rgba(0,0,0,0.55)_80%,rgba(22,209,214,0.10)_100%)]',
          '[box-shadow:0px_0px_6px_0px_#16D1D633,0px_0px_3px_0px_#16D1D622_inset]',
        ),
        error: 'border-error-accent-highlight bg-error-accent text-error',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive: cn(
          'relative overflow-hidden border border-[color:var(--destructive)] bg-transparent text-foreground',
          'before:absolute before:inset-0 before:-z-[1] before:opacity-100',
          'before:bg-[radial-gradient(circle_at_top,var(--destructive)_0%,rgba(0,0,0,0.55)_80%,rgba(0,0,0,0)_100%)]',
          '[box-shadow:0px_0px_6px_0px_var(--destructive),0px_0px_3px_0px_var(--destructive)_inset]',
        ),
        outline: 'text-foreground border-border bg-transparent',
      },
      size: {
        sm: 'h-6 px-2 rounded-md',
        md: 'h-8 px-3 rounded-md',
        lg: 'h-10 px-4 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
)

export interface UiBadgeProps
  extends
    React.ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof uiBadgeVariants> {}

function UiBadge({ className, variant, size, ...props }: UiBadgeProps) {
  return (
    <div
      className={cn(uiBadgeVariants({ variant, size }), className)}
      {...props}
    />
  )
}

export { UiBadge, uiBadgeVariants }
